"use client"
import { LinkButton } from "@/components/LinkButton"
import { LinkIconButton } from "@/components/LinkIconButton"
import { Logo } from "@/components/Logo"
import { AAAIcon } from "@/components/icons/AAAIcon"
import { EventsIcon } from "@/components/icons/EventsIcon"
import { ExploreIcon } from "@/components/icons/ExploreIcon"
import { InnovationsIcon } from "@/components/icons/InnovationsIcon"
import { ResourcesIcon } from "@/components/icons/ResourcesIcon"
import { ToolboxIcon } from "@/components/icons/ToolboxIcon"
import { TrendBabyIcon } from "@/components/icons/TrendBabyIcon"
import { TrendsIcon } from "@/components/icons/TrendsIcon"
import { Button } from "@/components/ui/Button"
import { DisabledFeatures } from "@/lib/static/disabledFeatures"
import { cn } from "@/lib/tailwind"
import { ChevronDown, ChevronUp, GraduationCapIcon, LifeBuoyIcon } from "lucide-react"
import Image from "next/image"
import Link, { type LinkProps } from "next/link"
import { usePathname } from "next/navigation"
import * as React from "react"

interface Props {
  disabledFeatures: string[]
}

export function SidebarLinks({ disabledFeatures }: Props) {
  return (
    <div className="relative flex h-full flex-col">
      <div className="flex-1 overflow-y-auto p-2 pb-20 md:pb-22">
        <Link prefetch={false} href="/" className="mb-4 block pr-16 md:mb-0 md:hidden">
          <Logo className="w-full" />
        </Link>
        <div className="space-y-2">
          <HomeSidebarToggle title="Discover" pathMatch={["/", "/daily"]} LeftIcon={ExploreIcon}>
            <HomeSidebarLink href="/">For You</HomeSidebarLink>
            <HomeSidebarLink href="/daily">Daily</HomeSidebarLink>
          </HomeSidebarToggle>

          <hr />

          <HomeSidebarToggle
            title="Trends & Insights"
            pathMatch={["/subtrends", "/megatrends", "/macrotrends", "/reports", "/stats", "/industries", "/regions"]}
            LeftIcon={TrendsIcon}
          >
            <HomeSidebarLink href="/subtrends" pathMatch={["/subtrends", "/megatrends", "/macrotrends"]}>
              Trends
            </HomeSidebarLink>
            <HomeSidebarLink href="/reports">Trend Reports</HomeSidebarLink>
            <HomeSidebarLink href="/stats">Stats</HomeSidebarLink>
            <HomeSidebarLink href="/industries">Industries</HomeSidebarLink>
            <HomeSidebarLink href="/regions">Regions</HomeSidebarLink>
          </HomeSidebarToggle>

          <hr />

          <HomeSidebarLink size="lg" href="/innovations" LeftIcon={InnovationsIcon}>
            Innovations
          </HomeSidebarLink>

          <hr />

          <HomeSidebarToggle title="Webinars & Events" LeftIcon={EventsIcon}>
            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/trend-events">
              Webinars
            </HomeSidebarLink>
            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/meetups">
              Meetups
            </HomeSidebarLink>
          </HomeSidebarToggle>

          {!disabledFeatures.includes(DisabledFeatures.TrendBaby) && (
            <>
              <hr />

              <HomeSidebarLink
                size="lg"
                href="/trendbaby"
                pathMatch={["/productlab", "/campaigncrafter", "/scenarioooh", "/productboost", "/agi-ventures"]}
                LeftIcon={TrendBabyIcon}
              >
                AI x Ideation
              </HomeSidebarLink>
            </>
          )}

          <hr />
          <HomeSidebarLink
            target="_blank"
            href="https://info.trendwatching.com/training"
            size="lg"
            LeftIcon={({ isSelected }) => (
              <GraduationCapIcon size={18} className={cn("stroke-[1.5px]", isSelected && "text-primary")} />
            )}
          >
            Training
          </HomeSidebarLink>

          <HomeSidebarLink href="/toolbox" size="lg" LeftIcon={ToolboxIcon}>
            Toolbox
          </HomeSidebarLink>

          <hr />

          <HomeSidebarLink target="_blank" size="lg" href="https://info.trendwatching.com/ask-an-analyst" LeftIcon={AAAIcon}>
            Ask An Analyst
          </HomeSidebarLink>

          <hr />

          <HomeSidebarToggle title="Resources" LeftIcon={ResourcesIcon}>
            <HomeSidebarLink href="/methodology">Methodology</HomeSidebarLink>

            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/ai-consumer-trends">
              AI ✨
            </HomeSidebarLink>
            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/feedback-and-fixes">
              Feedback
            </HomeSidebarLink>
            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/speaking-intro">
              Speaking
            </HomeSidebarLink>
            <HomeSidebarLink target="_blank" href="https://info.trendwatching.com/about">
              About
            </HomeSidebarLink>
          </HomeSidebarToggle>
          <HomeSidebarLink
            target="_blank"
            size="lg"
            LeftIcon={(props) => <LifeBuoyIcon size={18} className={cn("stroke-[1.5px]", props.isSelected && "text-primary")} />}
            href="https://info.trendwatching.com/support"
          >
            Support
          </HomeSidebarLink>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full bg-background p-4">
        <div className="flex space-x-4 md:justify-between md:space-x-0">
          <LinkIconButton
            variant="ghost"
            href="https://www.instagram.com/trendwatching_com"
            target="_blank"
            size="sm"
            className="size-9 dark:invert"
            icon={<Image src="/instagram-icon.svg" alt="Instagram" width={20} height={20} />}
          />
          <LinkIconButton
            variant="ghost"
            href="https://www.linkedin.com/company/trendwatching-com"
            target="_blank"
            size="sm"
            className="size-9 dark:invert"
            icon={<Image src="/linkedin-icon.svg" alt="Linkedin" width={20} height={20} className="mb-1" />}
          />
          <LinkIconButton
            variant="ghost"
            href="https://info.trendwatching.com/slack"
            target="_blank"
            size="sm"
            className="size-9 dark:invert"
            icon={<Image src="/slack-icon.svg" alt="Slack" width={20} height={20} />}
          />
          <LinkIconButton
            variant="ghost"
            href="https://www.trendwatching.com/whatsapp"
            target="_blank"
            size="sm"
            className="size-9 dark:invert"
            icon={<Image src="/whatsapp-icon.svg" alt="Whatsapp" width={20} height={20} className="mb-0.5" />}
          />
        </div>
      </div>
    </div>
  )
}

function HomeSidebarToggle({
  title,
  pathMatch,
  LeftIcon,
  children,
}: {
  title: string
  pathMatch?: string[]
  LeftIcon: (props: { isSelected?: boolean; size?: number; className: string }) => React.JSX.Element
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const isActive =
    pathMatch?.includes("/") && pathname === "/"
      ? true
      : pathMatch?.filter((p) => p !== "/").some((p) => pathname?.startsWith(p)) || false

  const [isOpen, setIsOpen] = React.useState(isActive)
  return (
    <div className="space-y-1">
      <Button
        className="flex h-12 w-full items-center justify-between rounded-lg px-3"
        variant={isActive ? "secondary" : "ghost"}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center space-x-2">
          <LeftIcon isSelected={isActive} className="min-h-[18px] min-w-[18px] fill-black dark:fill-white" />
          <p className="text-wrap text-left text-sm leading-[16px]">{title}</p>
        </div>
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </Button>
      {isOpen && <div className="space-y-1 pl-2">{children}</div>}
    </div>
  )
}

interface HomeSidebarLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> {
  size?: "sm" | "lg"
  href: string
  pathMatch?: string[]
  LeftIcon?: (props: { isSelected?: boolean; size?: number; className: string }) => React.JSX.Element
  children: React.ReactNode
}

function HomeSidebarLink({ size = "sm", href, pathMatch, LeftIcon, ...props }: HomeSidebarLinkProps) {
  const pathname = usePathname()
  const isActive =
    href === "/" ? pathname === "/" : pathMatch ? pathMatch.some((p) => pathname?.startsWith(p)) : pathname?.startsWith(href)
  return (
    <div>
      <LinkButton
        {...props}
        href={href}
        className={cn("flex w-full justify-start px-3", size === "sm" ? "h-8" : "h-12")}
        variant={isActive ? "secondary" : "ghost"}
        leftIcon={LeftIcon && <LeftIcon isSelected={isActive} className="min-h-[18px] min-w-[18px] fill-black dark:fill-white" />}
      >
        <p className="text-sm">{props.children}</p>
      </LinkButton>
    </div>
  )
}
